import { useRef, useState } from "react";
import {
    buildStoredCanvasPortableArtifact,
    downloadPortableTenantViewArtifact,
    inspectPortableTenantViewFile,
    restorePortableTenantViewArtifact,
} from "../lib/tenantViewPortableUi.js";

const buttonClass =
    "cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-progress disabled:opacity-60";

const defaultServices = Object.freeze({
    buildArtifact: buildStoredCanvasPortableArtifact,
    downloadArtifact: downloadPortableTenantViewArtifact,
    inspectFile: inspectPortableTenantViewFile,
    restoreArtifact: restorePortableTenantViewArtifact,
});

function errorMessage(error) {
    return error instanceof Error ? error.message : String(error || "Unknown error");
}

function countLabel(count, singular) {
    return `${count} ${singular}${count === 1 ? "" : "s"}`;
}

function InventoryPreview({ label, inventory }) {
    return (
        <section className="grid gap-[5px]">
            <div className="flex items-baseline justify-between gap-[10px]">
                <h3 className="text-[10px] font-medium uppercase tracking-[0.06em] text-[color:var(--ink)]">
                    {label}
                </h3>
                <span className="text-[10px] text-[color:var(--muted)]">
                    {inventory.total} found
                    {inventory.truncated ? ` · showing ${inventory.findings.length}` : ""}
                </span>
            </div>
            {inventory.findings.length ? (
                <ul className="max-h-[120px] overflow-auto rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[9px] py-[6px]">
                    {inventory.findings.map((finding, index) => (
                        <li
                            key={`${finding.object_id}:${finding.kind}:${finding.json_pointer}:${index}`}
                            className="grid grid-cols-[auto_1fr] gap-x-[7px] py-[2px] text-[10px] leading-[1.35]"
                        >
                            <span className="text-[color:var(--muted)]">{finding.kind}</span>
                            <code className="min-w-0 break-all text-[color:var(--ink)]">
                                {finding.object_id}{finding.json_pointer || "/"}
                            </code>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-[10px] text-[color:var(--muted)]">No paths reported.</p>
            )}
        </section>
    );
}

function exportReview(artifact, canvasSlug) {
    return {
        artifact,
        canvasSlug,
        identifierFindings: artifact.identifier_findings,
        secretFindings: artifact.secret_findings,
    };
}

export default function PortableTenantViewArtifactActions({
    canvas = null,
    services = defaultServices,
    className = "",
}) {
    const fileInput = useRef(null);
    const [exporting, setExporting] = useState(false);
    const [exportPreview, setExportPreview] = useState(null);
    const [exportConfirmed, setExportConfirmed] = useState(false);
    const [inspecting, setInspecting] = useState(false);
    const [preview, setPreview] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [secretApprovals, setSecretApprovals] = useState({});
    const [restoring, setRestoring] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const backup = async () => {
        if (!canvas?.tenantViewId
            || exporting
            || inspecting
            || preview
            || exportPreview) return;
        setExporting(true);
        setError("");
        try {
            const artifact = await services.buildArtifact(canvas);
            const review = exportReview(artifact, canvas.slug);
            if (review.identifierFindings.total || review.secretFindings.total) {
                setExportPreview(review);
                setExportConfirmed(false);
            } else {
                await services.downloadArtifact(artifact, { canvasSlug: canvas.slug });
            }
        } catch (nextError) {
            setError(errorMessage(nextError));
        } finally {
            setExporting(false);
        }
    };

    const closeExport = () => {
        if (exporting) return;
        setExportPreview(null);
        setExportConfirmed(false);
        setError("");
    };

    const downloadExport = async () => {
        if (!exportPreview?.artifact || !exportConfirmed || exporting) return;
        setExporting(true);
        setError("");
        try {
            await services.downloadArtifact(exportPreview.artifact, {
                canvasSlug: exportPreview.canvasSlug,
            });
            setExportPreview(null);
            setExportConfirmed(false);
        } catch (nextError) {
            setError(errorMessage(nextError));
        } finally {
            setExporting(false);
        }
    };

    const chooseFile = () => {
        if (exporting || exportPreview || preview) return;
        setError("");
        fileInput.current?.click();
    };

    const inspect = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setInspecting(true);
        setPreview(null);
        setResult(null);
        setConfirmed(false);
        setSecretApprovals({});
        setError("");
        try {
            setPreview(await services.inspectFile(file));
        } catch (nextError) {
            setError(errorMessage(nextError));
        } finally {
            setInspecting(false);
        }
    };

    const close = () => {
        if (restoring) return;
        setPreview(null);
        setResult(null);
        setConfirmed(false);
        setSecretApprovals({});
        setError("");
    };

    const restore = async () => {
        if (!preview?.artifact || !confirmed || restoring) return;
        setRestoring(true);
        setResult(null);
        setError("");
        try {
            const secretOverrides = preview.preview.secretFindings.findings
                .filter((finding) => Object.prototype.hasOwnProperty.call(
                    secretApprovals,
                    `${finding.object_id}\u0000${finding.kind}\u0000${finding.json_pointer}`,
                ))
                .map((finding) => ({
                    objectId: finding.object_id,
                    kind: finding.kind,
                    jsonPointer: finding.json_pointer,
                    justification: secretApprovals[
                        `${finding.object_id}\u0000${finding.kind}\u0000${finding.json_pointer}`
                    ].trim(),
                }));
            setResult(await services.restoreArtifact(preview.artifact, {
                secretOverrides,
            }));
        } catch (nextError) {
            setError(errorMessage(nextError));
        } finally {
            setRestoring(false);
        }
    };

    const createdCount = result?.results?.filter((entry) =>
        ["created", "already_created"].includes(entry.status)).length || 0;
    const failed = result?.results?.filter((entry) => entry.status === "failed") || [];
    const durabilityWarnings = result?.results?.filter((entry) =>
        typeof entry.localDurabilityWarning === "string"
        && entry.localDurabilityWarning.trim()) || [];
    const recoveryPending = result?.complete === true && result?.durable === false;
    const retryAvailable = !result?.complete || recoveryPending;
    const deferredMessage = !result?.complete
        && typeof result?.message === "string"
        && result.message.trim()
        ? result.message.trim()
        : null;
    const secretApprovalsValid = preview?.preview.secretFindings.findings.every((finding) => {
        const key =
            `${finding.object_id}\u0000${finding.kind}\u0000${finding.json_pointer}`;
        if (!Object.prototype.hasOwnProperty.call(secretApprovals, key)) return true;
        const value = secretApprovals[key].trim();
        return value.length >= 8 && value.length <= 500;
    }) !== false;

    return (
        <div
            className={`flex flex-wrap items-center justify-end gap-[6px] ${className}`}
            aria-busy={exporting || inspecting || restoring ? "true" : undefined}
        >
            {canvas?.tenantViewId ? (
                <button
                    type="button"
                    className={buttonClass}
                    onClick={backup}
                    disabled={exporting || inspecting || Boolean(exportPreview || preview)}
                >
                    {exporting ? "Preparing backup…" : "Backup JSON"}
                </button>
            ) : null}
            <button
                type="button"
                className={buttonClass}
                onClick={chooseFile}
                disabled={inspecting || exporting || Boolean(exportPreview || preview)}
            >
                {inspecting ? "Checking backup…" : "Restore JSON"}
            </button>
            <input
                ref={fileInput}
                type="file"
                accept="application/json,.json"
                className="sr-only"
                aria-label="Choose a kernel and canvas backup"
                onChange={inspect}
            />
            {error && !preview && !exportPreview ? (
                <span role="alert" className="basis-full text-right text-[11px] text-red-400">
                    {error}
                </span>
            ) : null}

            {exportPreview ? (
                <div
                    className="fixed inset-0 z-[120] grid place-items-center bg-black/45 px-[18px] py-[24px]"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="portable-export-title"
                >
                    <div className="grid max-h-full w-full max-w-[600px] gap-[14px] overflow-auto rounded-[3px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[18px] py-[16px] shadow-2xl">
                        <header className="grid gap-[4px]">
                            <h2
                                id="portable-export-title"
                                className="text-[13px] font-medium text-[color:var(--ink)]"
                            >
                                Review backup before download
                            </h2>
                            <p className="text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                This backup contains identifier or secret-like paths. Review both
                                inventories before downloading it.
                            </p>
                        </header>

                        <InventoryPreview
                            label="Identifier paths"
                            inventory={exportPreview.identifierFindings}
                        />
                        <InventoryPreview
                            label="Secret-like paths"
                            inventory={exportPreview.secretFindings}
                        />

                        {error ? (
                            <p role="alert" className="text-[11px] text-red-400">{error}</p>
                        ) : null}

                        <label className="flex items-start gap-[7px] text-[11px] leading-[1.4] text-[color:var(--ink)]">
                            <input
                                type="checkbox"
                                checked={exportConfirmed}
                                onChange={(event) => setExportConfirmed(event.target.checked)}
                                disabled={exporting}
                                className="mt-[2px]"
                            />
                            <span>
                                I reviewed both inventories and want to download this backup.
                            </span>
                        </label>

                        <footer className="flex justify-end gap-[7px]">
                            <button
                                type="button"
                                className={buttonClass}
                                onClick={closeExport}
                                disabled={exporting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={buttonClass}
                                onClick={downloadExport}
                                disabled={!exportConfirmed || exporting}
                            >
                                {exporting ? "Downloading…" : "Download backup"}
                            </button>
                        </footer>
                    </div>
                </div>
            ) : null}

            {preview ? (
                <div
                    className="fixed inset-0 z-[120] grid place-items-center bg-black/45 px-[18px] py-[24px]"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="portable-restore-title"
                >
                    <div className="grid max-h-full w-full max-w-[600px] gap-[14px] overflow-auto rounded-[3px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[18px] py-[16px] shadow-2xl">
                        <header className="grid gap-[4px]">
                            <h2
                                id="portable-restore-title"
                                className="text-[13px] font-medium text-[color:var(--ink)]"
                            >
                                Restore kernel and canvas backup
                            </h2>
                            <p className="text-[11px] leading-[1.45] text-[color:var(--muted)]">
                                Restore creates new private drafts. Existing views are not changed or published.
                            </p>
                        </header>

                        <div className="flex flex-wrap gap-x-[12px] gap-y-[3px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[7px] text-[11px] text-[color:var(--ink)]">
                            <span>{countLabel(preview.preview.kernels, "kernel")}</span>
                            <span>{countLabel(preview.preview.canvases, "canvas")}</span>
                            <span>{countLabel(preview.preview.dependencies, "dependency")}</span>
                        </div>

                        <InventoryPreview
                            label="Identifier paths"
                            inventory={preview.preview.identifierFindings}
                        />
                        <InventoryPreview
                            label="Secret-like paths"
                            inventory={preview.preview.secretFindings}
                        />
                        {!result?.complete ? (
                            <label className="flex items-start gap-[7px] text-[11px] leading-[1.4] text-[color:var(--ink)]">
                                <input
                                    type="checkbox"
                                    checked={confirmed}
                                    onChange={(event) => setConfirmed(event.target.checked)}
                                    disabled={restoring}
                                    className="mt-[2px]"
                                />
                                <span>
                                    I reviewed the identifier and secret-like paths and want to
                                    create these private drafts.
                                </span>
                            </label>
                        ) : null}
                        {preview.preview.secretFindings.findings.length ? (
                            <section className="grid gap-[7px]">
                                <h3 className="text-[10px] font-medium uppercase tracking-[0.06em] text-amber-300">
                                    Findings to retain
                                </h3>
                                <p className="text-[10px] leading-[1.4] text-[color:var(--muted)]">
                                    Select and justify each finding you want retained. Unselected
                                    secret-like content is stripped from the private drafts.
                                </p>
                                {preview.preview.secretFindings.findings.map((finding) => {
                                    const key =
                                        `${finding.object_id}\u0000${finding.kind}\u0000${finding.json_pointer}`;
                                    const selected = Object.prototype.hasOwnProperty.call(
                                        secretApprovals,
                                        key,
                                    );
                                    return (
                                        <div
                                            key={key}
                                            className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] p-[8px]"
                                        >
                                            <label className="flex items-start gap-[7px] text-[10px] text-[color:var(--ink)]">
                                                <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    disabled={restoring}
                                                    onChange={(event) => setSecretApprovals(
                                                        (current) => {
                                                            const next = { ...current };
                                                            if (event.target.checked) next[key] = "";
                                                            else delete next[key];
                                                            return next;
                                                        },
                                                    )}
                                                    className="mt-[2px]"
                                                />
                                                <span className="min-w-0">
                                                    <span className="block text-amber-300">
                                                        {finding.kind}
                                                    </span>
                                                    <code className="block break-all text-[color:var(--muted)]">
                                                        {finding.object_id}
                                                        {finding.json_pointer || "/"}
                                                    </code>
                                                </span>
                                            </label>
                                            {selected ? (
                                                <textarea
                                                    className="mt-[7px] min-h-[60px] w-full resize-y rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[6px] text-[10px] text-[color:var(--ink)] outline-none"
                                                    value={secretApprovals[key]}
                                                    maxLength={500}
                                                    disabled={restoring}
                                                    placeholder="Explain why this is safe to retain (8 to 500 characters)"
                                                    onChange={(event) => setSecretApprovals(
                                                        (current) => ({
                                                            ...current,
                                                            [key]: event.target.value,
                                                        }),
                                                    )}
                                                />
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </section>
                        ) : null}

                        {result ? (
                            <div
                                role="status"
                                className={`rounded-[2px] border px-[10px] py-[8px] text-[11px] leading-[1.45] ${
                                    recoveryPending
                                        ? "border-amber-400/60 bg-amber-400/10 text-[color:var(--ink)]"
                                        : result.complete
                                        ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                                        : "border-red-400/60 bg-red-400/10 text-[color:var(--ink)]"
                                }`}
                            >
                                {recoveryPending
                                    ? `Cloud restore completed for ${createdCount} private drafts, but local recovery is not durable yet. Retry now before closing this backup.`
                                    : result.complete
                                    ? `Restore complete: ${createdCount} private drafts are available.`
                                    : deferredMessage
                                        || `Restore partially completed: ${createdCount} drafts available, ${failed.length} failed.`}
                                {durabilityWarnings.length ? (
                                    <ul className="mt-[5px] grid gap-[2px]">
                                        {durabilityWarnings.map((entry) => (
                                            <li key={`${entry.kind}:${entry.sourceId}`}>
                                                {entry.kind} {entry.sourceId}:{" "}
                                                {entry.localDurabilityWarning}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                                {failed.length ? (
                                    <ul className="mt-[5px] grid gap-[2px]">
                                        {failed.map((entry) => (
                                            <li key={`${entry.kind}:${entry.sourceId}`}>
                                                {entry.kind} {entry.sourceId}: {errorMessage(entry.error)}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </div>
                        ) : null}

                        {error ? (
                            <p role="alert" className="text-[11px] text-red-400">{error}</p>
                        ) : null}

                        <footer className="flex justify-end gap-[7px]">
                            <button
                                type="button"
                                className={buttonClass}
                                onClick={close}
                                disabled={restoring}
                            >
                                {recoveryPending
                                    ? "Close"
                                    : result?.complete
                                        ? "Done"
                                        : "Cancel"}
                            </button>
                            {retryAvailable ? (
                                <button
                                    type="button"
                                    className={buttonClass}
                                    onClick={restore}
                                    disabled={!confirmed || !secretApprovalsValid || restoring}
                                >
                                    {restoring
                                        ? "Restoring…"
                                        : recoveryPending
                                            ? "Retry local recovery"
                                            : result
                                            ? "Retry failed drafts"
                                            : "Restore private drafts"}
                                </button>
                            ) : null}
                        </footer>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export { defaultServices as portableTenantViewArtifactServices };
